import { useEffect, useState } from "react";
import EmptyState from "../../../../Components/EmptyState/EmptyState";
import { getCompanyCompletedJobs } from "../../../../services/applicationService";
import { addFavorite, removeFavorite } from "../../../../services/favoritesService";
import { submitEvaluation } from "../../../../services/evaluationService";
import { formatDatePtBr } from "../../../../utils/dateUtils";
import PunishmentModal from "../../../../Components/Punishmentmodal/Punishmentmodal";
import Toast from "../../../../Components/Toast/Toast"
import "./CompletedJobs.scss";

const IconStar = ({ filled, onClick, onMouseEnter, onMouseLeave }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill={filled ? "#f59e0b" : "none"}
    stroke="#f59e0b"
    strokeWidth="1.5"
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{ cursor: onClick ? "pointer" : "default" }}
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

function StarRatingInput({ value, onChange }) {
  const [hover, setHover] = useState(0);

  return (
    <div className="cj-star-input">
      {[1, 2, 3, 4, 5].map((star) => (
        <IconStar
          key={star}
          filled={star <= (hover || value)}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
        />
      ))}
    </div>
  );
}

export default function CompletedJobs() {
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [evaluatingId, setEvaluatingId] = useState(null);
  const [ratingDraft, setRatingDraft] = useState(0);
  const [commentDraft, setCommentDraft] = useState("");
  const [submittingEval, setSubmittingEval] = useState(false);
  const [reportingItem, setReportingItem] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const companyId = localStorage.getItem("userId");

  const fetchCompleted = async () => {
    try {
      setLoading(true);
      const data = await getCompanyCompletedJobs(companyId);
      setCompleted(data);
    } catch (err) {
      // console.error("Erro ao carregar vagas finalizadas:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) fetchCompleted();
  }, [companyId]);

  const toggleFavorite = async (item) => {
    const { freelancer } = item;
    setBusyId(item.id);
    try {
      if (freelancer.isFavorite) {
        await removeFavorite(freelancer.id);
      } else {
        await addFavorite(freelancer.id);
      }

      setCompleted(prev =>
        prev.map(c =>
          c.id === item.id
            ? { ...c, freelancer: { ...c.freelancer, isFavorite: !c.freelancer.isFavorite } }
            : c
        )
      );
    } catch (err) {
      console.error("Erro ao favoritar/desfavoritar:", err);
      showToast("Erro ao atualizar favorito.", "error");
    } finally {
      setBusyId(null);
    }
  };

  const openEvaluation = (item) => {
    setEvaluatingId(item.id);
    setRatingDraft(0);
    setCommentDraft("");
  };

  const cancelEvaluation = () => {
    setEvaluatingId(null);
    setRatingDraft(0);
    setCommentDraft("");
  };

  const openReportModal = (item) => {
    setReportingItem(item);
  };

  const closeReportModal = () => {
    setReportingItem(null);
  };

  // 🔧 renomeado pra não colidir com o import "submitEvaluation" do authService
  const handleSubmitEvaluation = async (item) => {
    if (ratingDraft === 0) {
      showToast("Selecione uma nota de 1 a 5 estrelas.", "info");
      return;
    }

    setSubmittingEval(true);
    try {
      await submitEvaluation(item.id, ratingDraft, commentDraft.trim() || null);

      setCompleted(prev =>
        prev.map(c =>
          c.id === item.id
            ? { ...c, evaluation: { rating: ratingDraft, comment: commentDraft.trim() || null } }
            : c
        )
      );

      cancelEvaluation();
      cancelEvaluation();
    } catch (err) {
      const data = err?.response?.data;
      const msg = typeof data === "string"
        ? data
        : data?.message || "Erro ao enviar avaliação.";
      showToast(msg, "error");
    } finally {
      setSubmittingEval(false);
    }
  };

  if (loading) {
    return <div className="cj-loading">Carregando trabalhos finalizados...</div>;
  }

  if (completed.length === 0) {
    return (
      <EmptyState message="Você ainda não possui trabalhos finalizados." />
    );
  }

  return (
    <section className="cj-wrapper">
      <div className="cj-grid">
        {completed.map((item) => {
          const isEvaluating = evaluatingId === item.id;
          const alreadyEvaluated = !!item.evaluation;

          return (
            <div key={item.id} className="cj-card">
              <div className="cj-header">
                {item.freelancer.avatarUrl ? (
                  <img
                    src={item.freelancer.avatarUrl}
                    alt={item.freelancer.nomeSocial?.trim() || item.freelancer.name}
                    className="cj-avatar"
                  />
                ) : (
                  <div className="cj-avatar-fallback">
                    {(item.freelancer.nomeSocial?.trim() || item.freelancer.name)
                      ?.slice(0, 2)
                      .toUpperCase()}
                  </div>
                )}

                <div className="cj-info">
                  <p className="cj-name">
                    {item.freelancer.nomeSocial?.trim() || item.freelancer.name}
                  </p>

                  {item.freelancer.phone && (
                    <p className="cj-phone">{item.freelancer.phone}</p>
                  )}
                </div>

                <button
                  className={`cj-fav-btn ${item.freelancer.isFavorite ? "cj-fav-btn--active" : ""}`}
                  onClick={() => toggleFavorite(item)}
                  disabled={busyId === item.id}
                  title={item.freelancer.isFavorite ? "Remover dos favoritos" : "Favoritar"}
                >
                  {busyId === item.id ? "..." : item.freelancer.isFavorite ? "★" : "☆"}
                </button>
              </div>

              <div className="cj-divider" />

              <div className="cj-job-info">
                <p className="cj-job-title">{item.job.title}</p>
                <span className="cj-job-date">📅 {formatDatePtBr(item.job.date)}</span>
                <span className="cj-job-payment">
                  R$ {Number(item.job.payment).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </span>
              </div>

              <div className="cj-status">
                {item.status === "completed" && (
                  <span className="cj-tag cj-tag--completed">✅ Concluído</span>
                )}
                {item.status === "early_exit" && (
                  <span className="cj-tag cj-tag--early-exit">⚠️ Saída antecipada</span>
                )}
              </div>

              <div className="cj-divider" />

              {alreadyEvaluated ? (
                <div className="cj-eval-done">
                  <span className="cj-eval-label">Sua avaliação:</span>
                  <div className="cj-star-display">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <IconStar key={s} filled={s <= item.evaluation.rating} />
                    ))}
                  </div>
                </div>
              ) : isEvaluating ? (
                <div className="cj-eval-form">
                  <StarRatingInput value={ratingDraft} onChange={setRatingDraft} />
                  <textarea
                    className="cj-eval-textarea"
                    placeholder="Comentário (opcional)"
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    rows={2}
                    maxLength={300}
                  />
                  <div className="cj-eval-actions">
                    <button
                      className="cj-eval-btn cj-eval-btn--cancel"
                      onClick={cancelEvaluation}
                      disabled={submittingEval}
                    >
                      Cancelar
                    </button>
                    <button
                      className="cj-eval-btn cj-eval-btn--submit"
                      onClick={() => handleSubmitEvaluation(item)}
                      disabled={submittingEval}
                    >
                      {submittingEval ? "Enviando..." : "Enviar avaliação"}
                    </button>
                  </div>
                </div>
              ) : (
                <button className="cj-eval-trigger" onClick={() => openEvaluation(item)}>
                  ⭐ Avaliar Freelancer
                </button>
              )}

              <button className="cj-report-btn" onClick={() => openReportModal(item)} type="button">
                ⚠️ Relatar problema
              </button>
            </div>
          );
        })}

        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      </div>

      {reportingItem && (
        <PunishmentModal
          freelancerId={reportingItem.freelancer.id}
          companyId={companyId}
          freelancerName={reportingItem.freelancer.name}
          jobApplicationId={reportingItem.id}
          onClose={closeReportModal}
          onSubmitted={closeReportModal}
        />
      )}
    </section>
  );
}